import { ActorEvent, ARRANGEMENT, Arrangement, CustomViewLayout, RecordLikeProtocol } from '@lighthouse/core'
import { FlowLayoutNode } from '@lighthouse/shared'
import { mergeRefs } from '@lighthouse/tools'
import React, { forwardRef, memo, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { useViewBlockContext } from '../../context'
import { Item } from './Item'
import { buildVerticalItemNodeData, geVerticalWidthSize } from './utils'

const ESTIMATED_WATERFALL_HEIGHT = 260

const Root = styled.div`
    position: relative;
`

const FrozenCard = styled.div`
    min-height: ${ESTIMATED_WATERFALL_HEIGHT}px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.04);
`

const WaterfallPlainColumns = styled.div<{ cols: number; gap: number }>`
    display: grid;
    grid-template-columns: ${({ cols }) => `repeat(${cols}, minmax(0, 1fr))`};
    gap: ${({ gap }) => `${gap}px`};
    align-items: start;
`

const WaterfallPlainColumn = styled.div<{ gap: number }>`
    display: flex;
    flex-direction: column;
    gap: ${({ gap }) => `${gap}px`};
`

interface Props {
    anchorId?: string
    node: FlowLayoutNode
    title: string
    readonly?: boolean
    gap: number
    cols: number
    layout: CustomViewLayout
    arrangement: Arrangement
    itemWidth: number
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
}

interface RecordWithIndex {
    recordId: string
    index: number
}

interface WaterfallColumnItem extends RecordWithIndex {
    top: number
    estimatedHeight: number
}

export const WaterfallScrollView = memo(
    forwardRef<HTMLDivElement, Props>((props, ref) => {
        const { node, anchorId, title, readonly, gap, cols, arrangement, itemWidth, onRecordClick } = props
        const innerRef = useRef<HTMLDivElement | null>(null)
        const itemHeightMapRef = useRef<Map<string, number>>(new Map())
        const columnCount = Math.max(1, Math.floor(cols))
        const { blockData, recordPoints } = useViewBlockContext()
        const { id } = blockData
        const widthSize = useMemo(() => geVerticalWidthSize(arrangement === ARRANGEMENT.fit, itemWidth), [arrangement, itemWidth])
        const itemDataMap = useMemo(() => {
            return new Map(
                recordPoints.map((recordPoint, index) => [
                    recordPoint.id,
                    buildVerticalItemNodeData({
                        node,
                        blockId: id,
                        recordId: recordPoint.id,
                        index,
                        readonly,
                        widthSize
                    })
                ])
            )
        }, [recordPoints, node, id, readonly, widthSize])

        const waterfallColumns = (() => {
            const columnHeights = Array.from({ length: columnCount }, () => 0)
            const columnsData = Array.from({ length: columnCount }, (_, index) => ({
                key: `waterfall-column-${index}`,
                items: [] as WaterfallColumnItem[],
                totalHeight: 0
            }))

            recordPoints.forEach((recordPoint, index) => {
                const estimatedHeight = itemHeightMapRef.current.get(recordPoint.id) ?? ESTIMATED_WATERFALL_HEIGHT
                const minColumnHeight = Math.min(...columnHeights)
                const targetColumnIndex = columnHeights.indexOf(minColumnHeight)
                const top = columnHeights[targetColumnIndex]
                columnsData[targetColumnIndex].items.push({
                    recordId: recordPoint.id,
                    index,
                    top,
                    estimatedHeight
                })
                columnHeights[targetColumnIndex] = top + estimatedHeight + gap
            })

            columnsData.forEach((column, index) => {
                column.totalHeight = Math.max(0, columnHeights[index] - gap)
            })

            return columnsData
        })()

        return (
            <Root ref={mergeRefs([innerRef, ref])} id={anchorId}>
                <WaterfallPlainColumns cols={columnCount} gap={gap}>
                    {waterfallColumns.map(columnData => {
                        return (
                            <WaterfallPlainColumn key={columnData.key} gap={gap}>
                                {columnData.items.map(item => {
                                    const itemData = itemDataMap.get(item.recordId)
                                    if (!itemData) {
                                        return <FrozenCard key={item.recordId} />
                                    }
                                    return (
                                        <Item
                                            key={item.recordId}
                                            data-record-id={item.recordId}
                                            style={{ height: 'fit-content' }}
                                            recordId={item.recordId}
                                            readonly={readonly}
                                            data={itemData}
                                            title={title}
                                            onRecordClick={onRecordClick}
                                        />
                                    )
                                })}
                            </WaterfallPlainColumn>
                        )
                    })}
                </WaterfallPlainColumns>
            </Root>
        )
    })
)
